/**
 * Record mode, the presses half (operator, 2026-10-16: "record mode", answers in `.notepads/timeline-pro-grade.md`
 * RECORD MODE). While a take runs, every press of a control on this device's canvas that a timeline can play (a
 * button, a guitar preset or snapshot, a Stream Deck key) is noted with the bar it happened on and the bar it was let
 * go on. The editor turns the take into changes on the armed lanes when it stops.
 *
 * Moves (the same day, operator: "presses and fader moves"): a fader, a knob or an XY pad tells the recorder each value
 * it sends while a take runs (`noteControlValue`, called from the control's own send), with the bar it happened on. The
 * editor turns each control's run of values into an automation curve.
 *
 * Pedals (Phase 3, the part that needs nothing new from OPBridge): a press of a controller OPBridge knows arrives as a
 * `control` frame naming the CONTROL, not the item. The control's binding on the unit's active page (following the
 * page's fall-through, or a binding on every page) names a named action, whose id is `<itemId>` or `<itemId>.<key>`
 * (`controllers/assign.ts`), so a press bound to a canvas control is recorded as a press of that control. The registry
 * is read when a take starts and again after each press (a page change on the pedal is seen from the next press). A
 * press can arrive on several sockets (the mesh), so one press of one control within 150 ms is counted once.
 *
 * Rules and named actions (OPBridge S324, `rule_fired_push`): a MIDI-trigger button's rule (`midiTrigger-<itemId>`,
 * its release `midiTrigger-<itemId>-release`) and a press of one of our named actions (`<itemId>` or `<itemId>.<key>`:
 * a Stream Deck key, OPMod, the dashboard) are reported by the bridge where they happened, and recorded as presses of
 * that item. A press heard twice (a pedal bound through the registry also fires our named action) is counted once:
 * one press of one item within 150 ms. Another device's tap is not reported yet (asked of OPBridge).
 *
 * It listens one level up, on the canvas items' own elements (`.editable-item[data-item-id]`), rather than inside each
 * kind's component: one listener hears every kind the same way, and recording cannot change how a control fires.
 * What it hears is the press itself, so a press that could not reach a computer is still recorded (it is the
 * performance being written down, not what the gear answered).
 */

import { createSignal } from 'solid-js';
import { activeProject } from '../layout/projectStore.ts';
import { onControlEvent, onFired, type ControlEvent, type FiredEvent } from '../opbridge/consumerWs.ts';
import { readRegistry } from '../opbridge/controllers.ts';
import type { ControllerRegistry } from '../opbridge/httpClient.ts';

/** The kinds a timeline can play (countIn.ts / bridgeSync FIREABLE). */
const RECORDABLE = new Set(['button', 'presetChange', 'snapshotChange', 'streamdeck']);

export interface RecordedPress {
  itemId: string;
  itemKind: string;
  label: string;
  /** Bar the press landed on (bars from the timeline's start). */
  downBar: number;
  /** Bar it was let go on; null while held, or when the take ended first. */
  upBar: number | null;
}

/** One value a fader, a knob or one axis of an XY pad sent during a take. */
export interface RecordedMove {
  itemId: string;
  /** The XY pad's axis; absent for a fader or a knob. */
  axis?: 'x' | 'y';
  /** Bar it was sent on (bars from the timeline's start). */
  bar: number;
  /** The 7-bit value it sent. */
  value: number;
}

interface Take {
  barNow: () => number | null;
  presses: RecordedPress[];
  open: Map<number, RecordedPress>;
  moves: RecordedMove[];
  /** The controller registry as last read, to find what a pedal press is bound to. */
  registry: ControllerRegistry | null;
  /** Presses held on a pedal, by its control id. */
  pedalOpen: Map<string, RecordedPress>;
  /** When each control's last press or release was counted (the mesh delivers one press on several sockets). */
  pedalSeen: Map<string, number>;
  /** When each ITEM's last remote press was recorded, so one press heard two ways counts once. */
  itemSeen: Map<string, number>;
  /** Presses held by a MIDI-trigger rule, by item id (its release rule lets go). */
  ruleOpen: Map<string, RecordedPress>;
  offControl: () => void;
  offFired: () => void;
}

let take: Take | null = null;
const [count, setCount] = createSignal(0);
const [moved, setMoved] = createSignal<ReadonlySet<string>>(new Set());

/** Presses noted in the take running now (reactive), for the recording bar. */
export const recordedCount = count;
/** Controls moved in the take running now (reactive): how many. */
export const recordedMoveCount = () => moved().size;

/** A fader, a knob or an XY pad axis sent `value` (its own send calls this; it does nothing unless a take runs). */
export function noteControlValue(itemId: string, axis: 'x' | 'y' | undefined, value: number): void {
  if (!take) return;
  const bar = take.barNow();
  if (bar === null) return;   // not playing yet: the count-in is not part of the take
  take.moves.push({ itemId, ...(axis ? { axis } : {}), bar, value });
  const key = `${itemId}|${axis ?? ''}`;
  if (!moved().has(key)) setMoved(new Set([...moved(), key]));
}

function itemOf(id: string): { kind: string; label: string } | null {
  for (const pg of activeProject()?.pages ?? []) {
    const it = pg.items.find((x) => x.id === id);
    if (it) {
      const named = it as { name?: string; label?: string };
      return { kind: it.kind, label: named.name || named.label || it.kind };
    }
  }
  return null;
}

function onDown(e: PointerEvent): void {
  if (!take || e.button > 0) return;
  const el = (e.target as Element | null)?.closest<HTMLElement>('.editable-item[data-item-id]');
  if (!el) return;
  const id = el.dataset['itemId'];
  const it = id ? itemOf(id) : null;
  if (!id || !it || !RECORDABLE.has(it.kind)) return;
  const bar = take.barNow();
  if (bar === null) return;   // not playing yet: a press during the count-in is not part of the take
  const p: RecordedPress = { itemId: id, itemKind: it.kind, label: it.label, downBar: bar, upBar: null };
  take.presses.push(p);
  take.open.set(e.pointerId, p);
  setCount(take.presses.length);
}

function onUp(e: PointerEvent): void {
  if (!take) return;
  const p = take.open.get(e.pointerId);
  if (!p) return;
  take.open.delete(e.pointerId);
  const bar = take.barNow();
  p.upBar = bar !== null && bar >= p.downBar ? bar : null;
}

/** The canvas item a controller's control is bound to on the unit's active page, or null. */
export function boundItemId(registry: ControllerRegistry, controlId: string): string | null {
  const onPage = (pageId: string | undefined) => registry.bindings.find((b) => b.controlId === controlId && b.pageId === pageId);
  let pageId = registry.activePageId;
  let hit: ControllerRegistry['bindings'][number] | undefined;
  for (let hops = 0; !hit && pageId && hops < 8; hops++) {
    hit = onPage(pageId);
    pageId = registry.pages.find((p) => p.id === pageId)?.fallsThroughTo;
  }
  hit ??= registry.bindings.find((b) => b.controlId === controlId && b.onEveryPage);
  return hit ? hit.actionId.split('.')[0]! : null;
}

function refreshRegistry(t: Take): void {
  void readRegistry().then((r) => { if (take === t && r.ok) t.registry = r.registry; }).catch(() => {});
}

function onPedal(e: ControlEvent): void {
  const t = take;
  if (!t || e.state === 'move') return;
  const key = `${e.unitId}|${e.controlId}|${e.state}`;
  const now = Date.now();
  if (now - (t.pedalSeen.get(key) ?? -1e9) < 150) return;
  t.pedalSeen.set(key, now);
  const bar = t.barNow();
  if (e.state === 'release') {
    const p = t.pedalOpen.get(e.controlId);
    if (p) { t.pedalOpen.delete(e.controlId); p.upBar = bar !== null && bar >= p.downBar ? bar : null; }
    return;
  }
  refreshRegistry(t);
  if (bar === null || !t.registry) return;
  const id = boundItemId(t.registry, e.controlId);
  const p = id ? recordRemote(t, id, bar) : null;
  if (p) t.pedalOpen.set(e.controlId, p);
}

/** Record a press of item `id` heard from outside this canvas, once per press (see the header). */
function recordRemote(t: Take, id: string, bar: number): RecordedPress | null {
  const it = itemOf(id);
  if (!it || !RECORDABLE.has(it.kind)) return null;
  const now = Date.now();
  if (now - (t.itemSeen.get(id) ?? -1e9) < 150) return null;
  t.itemSeen.set(id, now);
  const p: RecordedPress = { itemId: id, itemKind: it.kind, label: it.label, downBar: bar, upBar: null };
  t.presses.push(p);
  setCount(t.presses.length);
  return p;
}

const RULE_PREFIX = 'midiTrigger-';
function onFiredEvent(e: FiredEvent): void {
  const t = take;
  if (!t) return;
  const bar = t.barNow();
  if (e.kind === 'rule') {
    if (!e.ruleId.startsWith(RULE_PREFIX)) return;
    const rest = e.ruleId.slice(RULE_PREFIX.length);
    if (rest.endsWith('-release')) {
      const p = t.ruleOpen.get(rest.slice(0, -'-release'.length));
      if (p) { t.ruleOpen.delete(p.itemId); p.upBar = bar !== null && bar >= p.downBar ? bar : null; }
      return;
    }
    if (bar === null) return;
    const p = recordRemote(t, rest, bar);
    if (p) t.ruleOpen.set(rest, p);
    return;
  }
  if (!e.ok || bar === null) return;
  recordRemote(t, e.actionId.split('.')[0]!, bar);
}

/** Start a take: `barNow` says where the timeline is (null before it plays). */
export function startPressRecording(barNow: () => number | null): void {
  stopPressRecording();
  const t: Take = { barNow, presses: [], open: new Map(), moves: [], registry: null, pedalOpen: new Map(), pedalSeen: new Map(), itemSeen: new Map(), ruleOpen: new Map(), offControl: () => {}, offFired: () => {} };
  take = t;
  t.offControl = onControlEvent(onPedal);
  t.offFired = onFired(onFiredEvent);
  refreshRegistry(t);
  setCount(0);
  setMoved(new Set<string>());
  window.addEventListener('pointerdown', onDown, true);
  window.addEventListener('pointerup', onUp, true);
  window.addEventListener('pointercancel', onUp, true);
}

/** End the take and hand back its presses and moves (none when no take ran). */
export function stopPressRecording(): { presses: RecordedPress[]; moves: RecordedMove[] } {
  const t = take;
  take = null;
  t?.offControl();
  t?.offFired();
  window.removeEventListener('pointerdown', onDown, true);
  window.removeEventListener('pointerup', onUp, true);
  window.removeEventListener('pointercancel', onUp, true);
  setCount(0);
  setMoved(new Set<string>());
  return t ? { presses: t.presses, moves: t.moves } : { presses: [], moves: [] };
}
